import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { HeaderRightSlotProvider } from '@/contexts/HeaderRightSlotContext';
import { usePageFullScreenBackground } from '@/hooks/useFullScreenBackground';
import { Theme1BackgroundOverlay } from '@/components/Theme1BackgroundOverlay';
import AuthenticatedHeader from './components/Header/AuthenticatedHeader';
import { PostLoginTourGate } from '@/components/TourGuide';
import BottomNav from '@/components/BottomNav/BottomNav';

/** Routes that use fixed chrome header (`--wf-header-h`) + flex scroll body (no full-page scroll). */
const FIXED_CHROME_PATHS: string[] = [
  // Money
  '/money',
  '/money/add',
  '/money/cash-out',
  '/money/games',
  '/money/history',
  '/money/wallet',
  // Me
  '/me',
  '/me/details',
  '/me/messages',
  '/me/security',
  '/me/sound-motion',
  // Detailed money tools (kept URLs)
  '/account/finance/deposit',
  '/account/finance/payment-checkout',
  '/account/finance/wallet/deposit',
  '/account/wallet/deposit-checkout',
  '/account/wallet/payout-request',
];

function useFixedChromeLayout(pathname: string): boolean {
  return FIXED_CHROME_PATHS.some((path) => pathname === path);
}

interface AuthenticatedLayoutProps {
  header?: React.ReactNode | 'default' | 'none';
  bodyClassName?: string;
}

/**
 * Layout component for authenticated pages
 * Structure: Header + Body (mobile page title + Outlet)
 * On FIXED_CHROME_PATHS: fixed chrome header, flex body (scrollable); responsive.
 */
const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({
  header = 'default',
  bodyClassName = '',
}) => {
  const fullScreenBg = usePageFullScreenBackground();
  const location = useLocation();
  const useFixedChrome = useFixedChromeLayout(location.pathname);

  const headerNode = header === 'none' ? null : header === 'default' ? (
    <AuthenticatedHeader compact={useFixedChrome} />
  ) : (
    header
  );

  const shellBgClass = fullScreenBg.className.replace(/\bmin-h-screen\b/g, '').trim();
  const shellClassName = `${shellBgClass} flex flex-col flex-1 min-h-0 h-full max-h-full w-full overflow-hidden`;

  if (useFixedChrome) {
    return (
      <HeaderRightSlotProvider>
        <PostLoginTourGate />
        <div
          className={`relative z-1 ${shellClassName}`}
        >
          <Theme1BackgroundOverlay />
          {/* Header chrome — safe-area + min height (no fixed clip) */}
          {headerNode != null && (
            <div className="wf-header-chrome safe-area-top shrink-0" role="banner">
              {headerNode}
            </div>
          )}

          {/* Body: fills remaining space, scrollable — no page-title breadcrumb strip */}
          <main className={`relative z-1 flex-1 min-h-0 flex flex-col overflow-hidden ${bodyClassName}`}>
            <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
              <Outlet />
            </div>
          </main>
          <BottomNav />
        </div>
      </HeaderRightSlotProvider>
    );
  }

  return (
    <HeaderRightSlotProvider>
      <PostLoginTourGate />
      <div
        className={`relative z-1 safe-area-top ${shellClassName}`}
      >
        <Theme1BackgroundOverlay />
        {headerNode != null ? <div className="shrink-0">{headerNode}</div> : null}
        <main className={`relative z-1 flex-1 min-h-0 min-w-0 flex flex-col overflow-y-auto overflow-x-hidden cosmos-scroll-y ${bodyClassName}`}>
          <Outlet />
        </main>
        <BottomNav />
      </div>
    </HeaderRightSlotProvider>
  );
};

export default AuthenticatedLayout;
